/** The invite's `context` in the create / edit form (plan §5.4, D9).
 *
 *  The context is arbitrary JSON. The backend's registry (`demo/lib/onboarding/invites/context.py`
 *  KNOWN_KEYS) interprets four keys and keeps every other key as it is. The form gives each known
 *  key a field of its own and puts everything else in the "Advanced context" JSON editor. A known
 *  key may also be typed there, and is checked with the same rules, unless its field above is
 *  set too — then the form asks for one place.
 *
 *  `KNOWN_CONTEXT_KEYS` mirrors the backend registry; `inviteContext.contract.test.ts` pins it to
 *  the spec's OnboardingInviteContextSummary. The server re-validates everything, and the drawer
 *  shows the server's own interpreted / not-interpreted report.
 *
 *  `marketplace` (`READ_ONLY_CONTEXT_KEYS`) is interpreted too, but no form sets it: a booking
 *  system's marketplace app install writes it when it mints the invite, create refuses it, and an
 *  edit must send it unchanged. The edit form holds it aside (`splitContext`'s `fixed`), shows it
 *  read-only and puts it back as it was (`analyseContext`'s `fixed`). */

/** The largest context the backend accepts (serialised JSON, bytes). */
export const CONTEXT_MAX_BYTES = 16 * 1024;
export const PROMO_CODE_MAX = 64;
export const CONTRACT_REF_MAX = 200;

export interface KnownContextKey {
  description: string;
  /** null when valid; else the sentence the backend would answer. `restechs` is the codes of the
   *  easy registry, when the form could read it. */
  validate: (value: unknown, restechs: readonly string[] | null) => string | null;
}

function text(max: number) {
  return (value: unknown): string | null => {
    if (typeof value !== 'string') return 'A string.';
    const v = value.trim();
    if (!v) return 'Cannot be empty (leave the key out instead).';
    if (v.length > max) return `At most ${max} characters.`;
    return null;
  };
}

export const KNOWN_CONTEXT_KEYS: Record<string, KnownContextKey> = {
  restechs: {
    description: 'Only these booking systems are offered and accepted; absent = every available one.',
    validate: (value, restechs) => {
      if (!Array.isArray(value) || !value.every((v) => typeof v === 'string')) {
        return 'A list of booking-system codes.';
      }
      const codes = value.map((v) => v.trim().toLowerCase());
      if (codes.some((c) => !c)) return 'Booking-system codes cannot be empty.';
      if (codes.length === 0) return 'Name at least one booking system, or leave the key out to offer every one.';
      if (restechs) {
        const unknown = codes.find((c) => !restechs.includes(c));
        if (unknown) return `Unknown booking system '${unknown}' (available: ${restechs.join(', ') || 'none'}).`;
      }
      return null;
    },
  },
  allow_code_edit: {
    description: 'The client may edit the tenant code (D15); default false = derived and read-only.',
    validate: (value) => (typeof value === 'boolean' ? null : 'true or false.'),
  },
  promo_code: {
    description: 'Stored and copied to the tenant.',
    validate: text(PROMO_CODE_MAX),
  },
  contract_ref: {
    description: 'A free reference to the contract.',
    validate: text(CONTRACT_REF_MAX),
  },
};

/** Interpreted keys no form may set, with the sentence the backend answers when one is typed. */
export const READ_ONLY_CONTEXT_KEYS: Record<string, string> = {
  marketplace: 'Set only by a marketplace app install.',
};

// Own keys only: a context key named "constructor" or "toString" is not a known key.
const isInterpreted = (key: string) => Object.hasOwn(KNOWN_CONTEXT_KEYS, key) || Object.hasOwn(READ_ONLY_CONTEXT_KEYS, key);

/** The values of the form's own fields for the known keys. */
export interface StructuredContext {
  restechs: string[];
  allowCodeEdit: boolean;
  promoCode: string;
  contractRef: string;
}

export const EMPTY_STRUCTURED: StructuredContext = {
  restechs: [],
  allowCodeEdit: false,
  promoCode: '',
  contractRef: '',
};

/** The known keys the fields set (an empty field sets nothing). */
export function structuredToContext(s: StructuredContext): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const restechs = [...new Set(s.restechs.map((r) => r.trim().toLowerCase()).filter(Boolean))];
  if (restechs.length) out.restechs = restechs;
  if (s.allowCodeEdit) out.allow_code_edit = true;
  if (s.promoCode.trim()) out.promo_code = s.promoCode.trim();
  if (s.contractRef.trim()) out.contract_ref = s.contractRef.trim();
  return out;
}

export interface ContextAnalysis {
  /** The context to send; null while anything is invalid. */
  context: Record<string, unknown> | null;
  /** The advanced editor's own problem (not JSON, not an object, too large). */
  advancedError: string | null;
  /** Per-key problems of the advanced editor's known keys (invalid, or also set above). */
  keyErrors: Record<string, string>;
  /** Keys of the resulting context the backend interprets / keeps as they are. */
  interpreted: string[];
  uninterpreted: string[];
}

/** The advanced editor's text as an object: blank = `{}`. */
export function parseAdvanced(textValue: string): { value: Record<string, unknown> | null; error: string | null } {
  const t = textValue.trim();
  if (!t) return { value: {}, error: null };
  let parsed: unknown;
  try {
    parsed = JSON.parse(t);
  } catch (e) {
    return { value: null, error: `Not valid JSON: ${(e as Error).message}` };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { value: null, error: 'A JSON object, e.g. {"campaign": "ITB 2027"}.' };
  }
  return { value: parsed as Record<string, unknown>, error: null };
}

/** Check the advanced editor against the fields and merge both into the context to send.
 *  `fixed`: the read-only keys an existing invite carries (`splitContext`), sent back as they are. */
export function analyseContext(
  advancedText: string,
  structured: StructuredContext,
  restechs: readonly string[] | null,
  fixed: Record<string, unknown> = {},
): ContextAnalysis {
  const fromFields = structuredToContext(structured);
  const { value: advanced, error } = parseAdvanced(advancedText);
  const keyErrors: Record<string, string> = {};
  if (advanced) {
    for (const [key, value] of Object.entries(advanced)) {
      const readOnly = Object.hasOwn(READ_ONLY_CONTEXT_KEYS, key) ? READ_ONLY_CONTEXT_KEYS[key] : undefined;
      if (readOnly) {
        keyErrors[key] = readOnly;
        continue;
      }
      const known = Object.hasOwn(KNOWN_CONTEXT_KEYS, key) ? KNOWN_CONTEXT_KEYS[key] : undefined;
      if (!known) continue;
      if (key in fromFields) {
        keyErrors[key] = `“${key}” is also set by its field above — keep one.`;
        continue;
      }
      const problem = known.validate(value, restechs);
      if (problem) keyErrors[key] = problem;
    }
  }
  const merged = { ...(advanced ?? {}), ...fromFields, ...fixed };
  const keys = Object.keys(merged).sort();
  const interpreted = keys.filter(isInterpreted);
  const uninterpreted = keys.filter((k) => !isInterpreted(k));
  let advancedError = error;
  if (!advancedError && advanced && new TextEncoder().encode(JSON.stringify(merged)).length > CONTEXT_MAX_BYTES) {
    advancedError = `The whole context is at most ${CONTEXT_MAX_BYTES / 1024} KB of JSON.`;
  }
  const valid = !advancedError && Object.keys(keyErrors).length === 0;
  return { context: valid ? merged : null, advancedError, keyErrors, interpreted, uninterpreted };
}

/** An existing context split into the fields, the read-only keys (`fixed`: kept out of every
 *  editor, sent back unchanged) and the advanced editor's text (edit form). Values a field cannot
 *  show (a malformed `restechs`, a non-string promo code) stay in the editor, where they are
 *  checked and can be fixed. */
export function splitContext(context: Record<string, unknown> | null | undefined): {
  structured: StructuredContext;
  fixed: Record<string, unknown>;
  advancedText: string;
} {
  const rest: Record<string, unknown> = { ...(context ?? {}) };
  const fixed: Record<string, unknown> = {};
  for (const key of Object.keys(READ_ONLY_CONTEXT_KEYS)) {
    if (key in rest) {
      fixed[key] = rest[key];
      delete rest[key];
    }
  }
  const structured: StructuredContext = { ...EMPTY_STRUCTURED, restechs: [] };
  const r = rest.restechs;
  if (Array.isArray(r) && r.length > 0 && r.every((v) => typeof v === 'string' && v.trim())) {
    structured.restechs = r as string[];
    delete rest.restechs;
  }
  if (rest.allow_code_edit === true) {
    structured.allowCodeEdit = true;
    delete rest.allow_code_edit;
  } else if (rest.allow_code_edit === false) {
    delete rest.allow_code_edit; // the default
  }
  if (typeof rest.promo_code === 'string' && rest.promo_code.trim()) {
    structured.promoCode = rest.promo_code;
    delete rest.promo_code;
  }
  if (typeof rest.contract_ref === 'string' && rest.contract_ref.trim()) {
    structured.contractRef = rest.contract_ref;
    delete rest.contract_ref;
  }
  return { structured, fixed, advancedText: Object.keys(rest).length ? JSON.stringify(rest, null, 2) : '' };
}

/** Order-insensitive deep equality of two JSON values (did the edit change the context?). */
export function sameJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b || a === null || b === null || typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    const bb = b as unknown[];
    return a.length === bb.length && a.every((v, i) => sameJson(v, bb[i]));
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const ak = Object.keys(ao);
  return ak.length === Object.keys(bo).length && ak.every((k) => k in bo && sameJson(ao[k], bo[k]));
}
